// Image utility functions for cropping and responsive images

export interface Cropping {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Ratio {
  x: number;
  y: number;
}

export interface CroppedImageOptions {
  path: string;
  width: number;
  aspectRatio?: Ratio;
  cropping?: Cropping | null;
  devicePixelRatio?: DevicePixelRatio;
}

export interface ResponsiveImageSource {
  srcset: string;
  media: string;
}

export interface ResponsiveImageData {
  sources: ResponsiveImageSource[];
  fallbackSrc: string;
}

export interface ResponsiveImageOptions {
  path: string;
  aspectRatio?: Ratio;
  cropping?: Cropping | null;
  size?: "small" | "medium" | "large";
}

type DevicePixelRatio = 1 | 2 | 3;

// Common aspect ratios
export const ASPECT_RATIOS = {
  default: { x: 16, y: 9 },
  square: { x: 1, y: 1 },
  portrait: { x: 3, y: 4 },
  landscape: { x: 4, y: 3 },
  widescreen: { x: 16, y: 9 },
} as const;

/**
 * Generates a simple cropped image URL for a specific size
 */
export function generateCroppedImageUrl(options: CroppedImageOptions): string {
  const {
    path,
    width,
    aspectRatio = ASPECT_RATIOS.square,
    cropping,
    devicePixelRatio = 2,
  } = options;

  // Limit DPR to 2 for larger images to optimize performance
  const optimizedDpr =
    width >= 600 && devicePixelRatio > 2 ? 2 : devicePixelRatio;

  // Standard cloudinary optimization parameters
  const optimizationParams = "f_auto,q_auto,g_auto";

  // Build cropping parameters if provided
  const cropParams = cropping
    ? `c_crop,x_${cropping.x},y_${cropping.y},w_${cropping.width},h_${cropping.height}/`
    : "";

  // Build final transformation parameters
  const transformParams = `c_fill,w_${width * optimizedDpr},ar_${
    aspectRatio.x
  }:${aspectRatio.y},${optimizationParams}`;

  return `https://images2.minutemediacdn.com/${cropParams}${transformParams}/${path}`;
}

/**
 * Generates responsive image data for picture elements with srcset
 */
export function generateResponsiveImageData(options: ResponsiveImageOptions): ResponsiveImageData {
  const { path, aspectRatio = ASPECT_RATIOS.square, cropping, size = "medium" } = options;

  // Define different breakpoint sets based on image size usage
  const breakpointSets = {
    small: [
      { media: '(max-width: 719px)', baseWidth: 36 },
      { media: '(max-width: 1079px)', baseWidth: 36 },
      { media: '(min-width: 1080px)', baseWidth: 36 },
    ],
    medium: [
      { media: '(max-width: 719px)', baseWidth: 128 },
      { media: '(max-width: 1079px)', baseWidth: 128 },
      { media: '(min-width: 1080px)', baseWidth: 220 },
    ],
    large: [
      { media: '(max-width: 719px)', baseWidth: 360 },
      { media: '(max-width: 1079px)', baseWidth: 600 },
      { media: '(min-width: 1080px)', baseWidth: 800 },
    ],
  };

  const breakpoints = breakpointSets[size];

  const sources: ResponsiveImageSource[] = breakpoints.map((breakpoint) => {
    const densities = [1, 2, 3];
    const srcsetParts = densities.map((density) => {
      const width = breakpoint.baseWidth * density;
      const url = generateCroppedImageUrl({
        path,
        width,
        aspectRatio,
        cropping,
        devicePixelRatio: density as DevicePixelRatio, // Use actual density for proper image quality
      });
      return `${url} ${density}x`;
    });

    return {
      srcset: srcsetParts.join(', '),
      media: breakpoint.media,
    };
  });

  // Generate fallback image (1x density, mobile size)
  const fallbackWidth = breakpointSets[size][0].baseWidth;
  const fallbackSrc = generateCroppedImageUrl({
    path,
    width: fallbackWidth,
    aspectRatio,
    cropping,
    devicePixelRatio: 1,
  });

  return {
    sources,
    fallbackSrc,
  };
}
